import { Link, useNavigate } from "react-router-dom";
import { authService } from "@/services/authService";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  LogIn,
  UserPlus,
  Building2,
  MessageCircle,
  User,
  Calendar,
  Heart,
  Key,
  Home,
  Bell,
  Handshake,
  Shield,
  FileText,
  Sparkles,
  CreditCard,
  ChevronDown,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuthState } from "@/hooks/useAuthState";
import {
  canShowLiveSubscriptionPromo,
  isOwnerUser,
  resolveUserType,
} from "@/lib/livePlanAccess";
import { useFavoritos } from "@/hooks/useFavoritos";
import { useMessageNotifications } from "@/hooks/useMessageNotifications";
import { useNotifications } from "@/hooks/useNotifications";
import { useContactNotifications } from "@/hooks/useContactNotifications";
import { UserPhotoService } from "@/services/userPhotoService";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import logo from "@/assets/live-stay-logo.png";

interface NavbarProps {
  isAuthenticated?: boolean;
  isOwner?: boolean;
  userName?: string;
  userPhoto?: string;
  userId?: number;
  userType?: string;
  rightActionsCard?: boolean;
  cleanHeader?: boolean;
}

const planLabel = (
  plan: string | null | undefined,
  userType: string | null | undefined,
): string | null => {
  if (userType !== "owner") return null;
  if (plan === "live") return "Cliente Live";
  if (plan === "porto2life") return "Cliente Porto 2 Life";
  return null;
};

const Navbar = ({
  isAuthenticated = false,
  isOwner = false,
  userName,
  userPhoto,
  userId,
  userType,
  rightActionsCard = true,
  cleanHeader = false,
}: NavbarProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { subscriptionPlan, userType: authUserType, user: authUser } = useAuthState();
  const effectiveUserType = resolveUserType(authUserType, authUser);
  const { favoritos } = useFavoritos();
  const { unreadCount, hasUnreadMessages, lastConversationId } = useMessageNotifications();
  const { unreadCount: notificationUnreadCount, hasUnreadNotifications } = useNotifications();
  useContactNotifications();

  const showAssineLive =
    isAuthenticated &&
    canShowLiveSubscriptionPromo(authUserType, subscriptionPlan, authUser);
  const canManageProperties = isOwner && (subscriptionPlan === "porto2life" || subscriptionPlan === "live");
  const favoritosCount = favoritos.length;

  // Estilo overlay (texto branco/transparente) só é usado quando a página
  // explicitamente coloca o Navbar sobre um banner via `cleanHeader`.
  // Páginas sem hero (ex.: /imovel/:id, /termos, /privacidade) usam o
  // header escuro padrão para garantir contraste e legibilidade.
  const isOverlay = cleanHeader;

  const handleLogout = () => {
    authService.logout();
    toast({
      title: "Logout realizado com sucesso!",
      description: "Até logo! Volte sempre à LiveStay.",
    });
    window.location.href = "/";
  };

  const handleMessageNotificationClick = () => {
    if (lastConversationId) {
      navigate(`/minha-area/mensagens?conversation=${lastConversationId}`);
    } else {
      navigate("/minha-area/mensagens");
    }
  };

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full transition-colors",
        isOverlay
          ? "border-b border-white/10 bg-background/30 backdrop-blur-xl supports-[backdrop-filter]:bg-background/30"
          : "border-b border-border bg-background/85 backdrop-blur-xl supports-[backdrop-filter]:bg-background/70",
      )}
    >
      <div className="container mx-auto flex items-center justify-between px-3 sm:px-6 py-3 gap-3">
        {/* Logo */}
        <Link
          to="/"
          aria-label="LiveStay"
          className="flex items-center group flex-shrink-0 rounded-xl px-3 py-1.5 bg-white hover:bg-white/95 transition-colors shadow-sm"
        >
          <img
            src={logo}
            alt="LiveStay"
            className="h-10 sm:h-12 md:h-14 w-auto object-contain"
          />
        </Link>

        {/* Right actions */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {showAssineLive && (
            <div className="relative shrink-0">
              {/* Halo pulsante para destaque, especialmente em mobile */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 rounded-full bg-orange-400/60 animate-ping opacity-75"
              />
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate("/minha-area/assinar-live")}
                className={cn(
                  "relative inline-flex gap-1.5 text-xs sm:text-sm px-2.5 sm:px-3 h-9 sm:h-10",
                  "bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600",
                  "text-white border-0 rounded-full shadow-md shadow-orange-500/30 ring-2 ring-white/40",
                )}
                aria-label="Assine o plano Live"
                title="Assine o plano Live"
              >
                <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="font-semibold">Assine Live</span>
              </Button>
            </div>
          )}

          {isAuthenticated && (
            <div className="hidden md:flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleMessageNotificationClick}
                className={cn(
                  "relative size-10 rounded-full",
                  isOverlay
                    ? "text-white hover:bg-white/15"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
                aria-label="Mensagens"
                title={hasUnreadMessages ? `${unreadCount} mensagens não lidas` : "Mensagens"}
              >
                <MessageCircle className="h-5 w-5" />
                {hasUnreadMessages && (
                  <span className="absolute top-1 right-1 size-2 rounded-full bg-red-500 ring-2 ring-background" />
                )}
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/minha-area/notificacoes")}
                className={cn(
                  "relative size-10 rounded-full",
                  isOverlay
                    ? "text-white hover:bg-white/15"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
                aria-label="Notificações"
                title={
                  hasUnreadNotifications
                    ? `${notificationUnreadCount} notificações não lidas`
                    : "Notificações"
                }
              >
                <Bell className="h-5 w-5" />
                {hasUnreadNotifications && (
                  <span className="absolute top-2 right-2 size-2 rounded-full bg-accent ring-2 ring-background" />
                )}
              </Button>
            </div>
          )}

          {isAuthenticated ? (
            <>
              {userType === "admin" ? (
                <Button
                  onClick={() => navigate("/admin/dashboard")}
                  className="gap-2 rounded-full text-xs sm:text-sm px-3 sm:px-4 h-10 bg-primary hover:bg-primary/90 text-primary-foreground"
                  aria-label="Admin"
                >
                  <Shield className="h-4 w-4" />
                  <span className="hidden sm:inline">Admin</span>
                </Button>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        // Layout do pill é responsivo e depende da presença
                        // do botão "Assine Live": com o CTA, no mobile o pill
                        // fica compacto (só avatar). Sem o CTA, sobra espaço
                        // para mostrar nome e plano também no mobile.
                        "flex items-center rounded-full transition-colors shadow-sm",
                        showAssineLive
                          ? "p-0.5 sm:gap-3 sm:pl-1 sm:pr-3 sm:py-1"
                          : "gap-2 pl-1 pr-2 py-1 sm:gap-3 sm:pr-3",
                        isOverlay
                          ? "border border-white/30 bg-white/20 backdrop-blur hover:bg-white/30 text-white"
                          : "border border-border bg-card hover:bg-muted text-foreground",
                      )}
                      aria-label="Menu da minha área"
                    >
                      <Avatar className="size-9 ring-2 ring-primary/40 shadow-sm">
                        <AvatarImage
                          src={
                            userPhoto && userId
                              ? UserPhotoService.getUserPhotoUrl(userId, userPhoto)
                              : undefined
                          }
                          alt={userName ?? "Usuário"}
                          className="object-cover"
                        />
                        <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                          {(userName || "U").charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div
                        className={cn(
                          "leading-tight text-left max-w-[140px] sm:max-w-[160px]",
                          showAssineLive ? "hidden sm:block" : "block",
                        )}
                      >
                        <div
                          className={cn(
                            "text-sm font-semibold truncate",
                            isOverlay ? "text-white drop-shadow-sm" : "text-foreground",
                          )}
                        >
                          {userName || "Minha conta"}
                        </div>
                        {planLabel(subscriptionPlan, effectiveUserType) && (
                          <div
                            className={cn(
                              "text-[10px] truncate",
                              isOverlay ? "text-white/80" : "text-muted-foreground",
                            )}
                          >
                            {planLabel(subscriptionPlan, effectiveUserType)}
                          </div>
                        )}
                      </div>
                      <ChevronDown
                        className={cn(
                          "h-4 w-4",
                          showAssineLive ? "hidden sm:block" : "block",
                          isOverlay ? "text-white/85" : "text-muted-foreground",
                        )}
                      />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem asChild>
                      <Link to="/minha-area" className="flex items-center gap-2 cursor-pointer">
                        <User className="h-4 w-4" />
                        Perfil
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/minha-area/reservas" className="flex items-center gap-2 cursor-pointer">
                        <Calendar className="h-4 w-4" />
                        Minhas Reservas
                      </Link>
                    </DropdownMenuItem>
                    {canManageProperties && (
                      <>
                        <DropdownMenuItem asChild>
                          <Link to="/minha-area/meus-imoveis" className="gap-2">
                            <Building2 className="h-4 w-4" />
                            Meus Imóveis
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/minha-area/solicitacoes-reservas" className="gap-2">
                            <Calendar className="h-4 w-4" />
                            Solicitações de Reservas
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/minha-area/documentos" className="gap-2">
                            <FileText className="h-4 w-4" />
                            Documentos
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/minha-area/mensagens" className="gap-2">
                        <MessageCircle className="h-4 w-4" />
                        Mensagens
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/minha-area/notificacoes" className="flex items-center gap-2 cursor-pointer">
                        <div className="relative">
                          <Bell className="h-4 w-4" />
                          {notificationUnreadCount > 0 && (
                            <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                              {notificationUnreadCount}
                            </span>
                          )}
                        </div>
                        Notificações
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/minha-area/favoritos" className="flex items-center gap-2 cursor-pointer">
                        <div className="relative">
                          <Heart className="h-4 w-4" />
                          {favoritosCount > 0 && (
                            <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                              {favoritosCount}
                            </span>
                          )}
                        </div>
                        Favoritos
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/minha-area/cadastro" className="flex items-center gap-2 cursor-pointer">
                        <User className="h-4 w-4" />
                        Meus Dados
                      </Link>
                    </DropdownMenuItem>
                    {isOwnerUser(effectiveUserType) && (
                      <DropdownMenuItem asChild>
                        <Link
                          to="/minha-area/minha-assinatura"
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <CreditCard className="h-4 w-4" />
                          Minha assinatura
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/minha-area/senha" className="flex items-center gap-2 cursor-pointer">
                        <Key className="h-4 w-4" />
                        Alterar Senha
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/" className="flex items-center gap-2 cursor-pointer">
                        <Home className="h-4 w-4" />
                        Voltar ao Início
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 cursor-pointer"
                    >
                      <LogIn className="h-4 w-4 mr-2" />
                      Sair
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => navigate("/entrar")}
                className={cn(
                  "gap-2 rounded-full text-xs sm:text-sm px-3 sm:px-4 h-9 sm:h-10",
                  isOverlay
                    ? "text-white hover:bg-white/15 hover:text-white"
                    : "text-foreground hover:bg-muted",
                )}
                aria-label="Entrar"
              >
                <LogIn className="h-4 w-4" />
                <span>Entrar</span>
              </Button>
              <Button
                onClick={() => navigate("/cadastro")}
                className="gap-2 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-xs sm:text-sm px-3 sm:px-4 h-9 sm:h-10 shadow-sm"
                aria-label="Cadastre-se"
              >
                <UserPlus className="h-4 w-4" />
                <span>Cadastre-se</span>
              </Button>
              <Button
                onClick={() => navigate("/cadastro-parceiro")}
                className="hidden md:inline-flex gap-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white text-sm px-4 h-10 shadow-sm"
              >
                <Handshake className="h-4 w-4" />
                Seja nosso Parceiro
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
